package com.schoolconnect.app.feature.classes

import com.schoolconnect.app.feature.common.ServerPagedListUiState
import com.schoolconnect.app.feature.common.ServerPagingState
import com.schoolconnect.app.feature.common.compareBySmartText
import com.schoolconnect.app.feature.common.toWindow
import com.schoolconnect.app.datasource.network.response.SchoolStatisticsResponse

data class ClassesSummaryUi(
    val total: Int = 0,
    val active: Int = 0,
    val inactive: Int = 0
)

enum class ClassSectionStatusUi(val label: String) {
    ACTIVE("Active"),
    INACTIVE("Inactive")
}

enum class ClassesSortField(val apiField: String) {
    CLASS_NAME("class_name"),
    SECTION_NAME("section_name"),
    SUBJECT_CODES("created_at"),
    SUBJECT_COUNT("created_at"),
    STATUS("is_active"),
    CREATED_AT("created_at"),
    UPDATED_AT("created_at")
}

enum class ClassesDialogMode {
    NONE,
    FILTER,
    EDITOR,
    DETAILS,
    DELETE_CONFIRM
}

data class ClassSectionRowUi(
    val id: String,
    val schoolId: String = "",
    val academicYearId: String = "",
    val className: String = "",
    val sectionName: String = "",
    val subjectCodes: String = "",
    val subjectCount: Int = 0,
    val status: ClassSectionStatusUi = ClassSectionStatusUi.ACTIVE,
    val createdAt: String = "",
    val updatedAt: String = "",
    val createdBy: String = "",
    val updatedBy: String = ""
)

data class ClassSectionFilterOption(
    val className: String,
    val sectionName: String
)

data class ClassSectionScopeOption(
    val label: String,
    val className: String = "",
    val sectionName: String = ""
)

internal const val ALL_CLASS_SECTIONS_LABEL = "All Classes / Sections"

internal fun classSectionScopeOptions(options: List<ClassSectionFilterOption>): List<ClassSectionScopeOption> = buildList {
    add(ClassSectionScopeOption(ALL_CLASS_SECTIONS_LABEL))
    options.distinct().sortedWith(
        compareBySmartText<ClassSectionFilterOption> { it.className }
            .then(compareBySmartText { it.sectionName })
    ).forEach { option ->
        add(ClassSectionScopeOption("${option.className} - ${option.sectionName}", option.className, option.sectionName))
    }
}.distinctBy { it.label }

internal fun selectedClassSectionScope(
    filters: ClassSectionFilterCriteria,
    options: List<ClassSectionScopeOption>
): ClassSectionScopeOption = options.firstOrNull { option ->
    option.className.equals(filters.classId, ignoreCase = true) &&
        option.sectionName.equals(filters.sectionId, ignoreCase = true)
} ?: options.firstOrNull { it.label == ALL_CLASS_SECTIONS_LABEL }
    ?: ClassSectionScopeOption(ALL_CLASS_SECTIONS_LABEL)

data class ClassSectionEditorDraft(
    val id: String = "",
    val schoolId: String = "",
    val academicYearId: String = "",
    val className: String = "",
    val sectionName: String = "",
    val subjectCodes: List<String> = emptyList()
)

data class ClassSectionEditorValidationErrors(
    val className: String = "",
    val sectionName: String = "",
    val subjectCodes: String = ""
) {
    val hasAnyError: Boolean
        get() = className.isNotBlank() || sectionName.isNotBlank() || subjectCodes.isNotBlank()
}

internal fun validateClassSectionEditor(draft: ClassSectionEditorDraft): ClassSectionEditorValidationErrors =
    ClassSectionEditorValidationErrors(
        className = if (draft.className.trim().isBlank()) "Class is required." else "",
        sectionName = if (draft.sectionName.trim().isBlank()) "Section is required." else "",
        subjectCodes = if (draft.subjectCodes.map(String::trim).none { it.isNotBlank() }) {
            "At least one subject code is required."
        } else {
            ""
        }
    )

data class ClassSectionFilterCriteria(
    val classId: String = "",
    val sectionId: String = "",
    val subjectCode: String = "",
    val createdFrom: String = "",
    val createdTo: String = ""
)

data class ClassesUiState(
    override val paging: ServerPagingState = ServerPagingState(),
    val isLoading: Boolean = false,
    val isAnalyticsLoading: Boolean = false,
    val isSaving: Boolean = false,
    val errorMessage: String = "",
    val apiErrorMessage: String = "",
    val infoMessage: String = "",
    val schoolId: String = "",
    val schoolLabel: String = "iConnect.School",
    val academicYearId: String = "",
    val academicYearLabel: String = "",
    val subjectCodeOptions: Map<String, String> = emptyMap(),
    val subjectSelectionOptions: Map<String, String> = emptyMap(),
    val classSectionFilterOptions: List<ClassSectionFilterOption> = emptyList(),
    val searchQuery: String = "",
    val filters: ClassSectionFilterCriteria = ClassSectionFilterCriteria(),
    val sortField: ClassesSortField = ClassesSortField.CLASS_NAME,
    val sortAscending: Boolean = true,
    val dialogMode: ClassesDialogMode = ClassesDialogMode.NONE,
    val activeClassId: String = "",
    val editorDraft: ClassSectionEditorDraft = ClassSectionEditorDraft(),
    val filterDraft: ClassSectionFilterCriteria = ClassSectionFilterCriteria(),
    val classes: List<ClassSectionRowUi> = emptyList(),
    val summary: ClassesSummaryUi = ClassesSummaryUi(),
    val analytics: SchoolStatisticsResponse? = null
) : ServerPagedListUiState

internal fun classFilterLabels(filters: ClassSectionFilterCriteria): List<String> = buildList {
    if (filters.classId.isNotBlank() || filters.sectionId.isNotBlank()) {
        val className = filters.classId.trim().ifBlank { "All Classes" }
        val sectionName = filters.sectionId.trim().ifBlank { "All Sections" }
        add("Class / Section: $className - $sectionName")
    }
    if (filters.subjectCode.isNotBlank()) add("Subject code: ${filters.subjectCode.trim()}")
    if (filters.createdFrom.isNotBlank() || filters.createdTo.isNotBlank()) {
        val from = filters.createdFrom.ifBlank { "Any" }
        val to = filters.createdTo.ifBlank { "Any" }
        add("Created: $from to $to")
    }
}

sealed interface ClassesUiEvent {
    data object BackClicked : ClassesUiEvent
    data object RefreshRequested : ClassesUiEvent
    data class SearchChanged(val query: String) : ClassesUiEvent
    data class SortChanged(val field: ClassesSortField) : ClassesUiEvent
    data class PageChanged(val pageIndex: Int) : ClassesUiEvent
    data class PageSizeChanged(val pageSize: Int) : ClassesUiEvent
    data object FilterClicked : ClassesUiEvent
    data object FilterDismissed : ClassesUiEvent
    data class FilterApplied(val filters: ClassSectionFilterCriteria) : ClassesUiEvent
    data class FilterClassSectionChanged(val className: String, val sectionName: String) : ClassesUiEvent
    data object FilterReset : ClassesUiEvent
    data object AddClicked : ClassesUiEvent
    data class ViewClicked(val classId: String) : ClassesUiEvent
    data class EditClicked(val classId: String) : ClassesUiEvent
    data class DeleteClicked(val classId: String) : ClassesUiEvent
    data object DeleteConfirmed : ClassesUiEvent
    data object DialogDismissed : ClassesUiEvent
    data object EditorSaveRequested : ClassesUiEvent
    data class EditorClassChanged(val value: String) : ClassesUiEvent
    data class EditorSectionChanged(val value: String) : ClassesUiEvent
    data class EditorSubjectCodesChanged(val values: List<String>) : ClassesUiEvent
    data class FilterSubjectCodeChanged(val value: String) : ClassesUiEvent
    data class FilterCreatedFromChanged(val value: String) : ClassesUiEvent
    data class FilterCreatedToChanged(val value: String) : ClassesUiEvent
    data object ClearMessages : ClassesUiEvent
}

internal data class ClassesPageUi(
    val items: List<ClassSectionRowUi>,
    val totalCount: Int,
    val startIndex: Int,
    val endIndex: Int,
    val pageCount: Int
)

internal fun classesSummary(items: List<ClassSectionRowUi>): ClassesSummaryUi {
    val active = items.count { it.status == ClassSectionStatusUi.ACTIVE }
    return ClassesSummaryUi(
        total = items.size,
        active = active,
        inactive = items.size - active
    )
}

internal fun classesPage(state: ClassesUiState): ClassesPageUi {
    val window = state.paging.toWindow(state.classes.size)
    return ClassesPageUi(
        items = state.classes,
        totalCount = window.totalCount,
        startIndex = window.startIndex,
        endIndex = window.endIndex,
        pageCount = window.pageCount
    )
}

internal fun classesComparator(
    field: ClassesSortField,
    ascending: Boolean
): Comparator<ClassSectionRowUi> {
    val comparator = when (field) {
        ClassesSortField.CLASS_NAME -> compareBySmartText<ClassSectionRowUi> { it.className }
        ClassesSortField.SECTION_NAME -> compareBySmartText<ClassSectionRowUi> { it.sectionName }
        ClassesSortField.SUBJECT_CODES -> compareBySmartText<ClassSectionRowUi> { it.subjectCodes }
        ClassesSortField.SUBJECT_COUNT -> compareBy<ClassSectionRowUi> { it.subjectCount }
        ClassesSortField.STATUS -> compareBy<ClassSectionRowUi> { if (it.status == ClassSectionStatusUi.ACTIVE) 0 else 1 }
        ClassesSortField.CREATED_AT -> compareBySmartText<ClassSectionRowUi> { it.createdAt }
        ClassesSortField.UPDATED_AT -> compareBySmartText<ClassSectionRowUi> { it.updatedAt }
    }
    return if (ascending) comparator else comparator.reversed()
}

internal fun classSectionStatusFromBoolean(isActive: Boolean): ClassSectionStatusUi =
    if (isActive) ClassSectionStatusUi.ACTIVE else ClassSectionStatusUi.INACTIVE

internal fun ClassSectionRowUi.toEditorDraft(): ClassSectionEditorDraft = ClassSectionEditorDraft(
    id = id,
    schoolId = schoolId,
    academicYearId = academicYearId,
    className = className,
    sectionName = sectionName,
    subjectCodes = subjectCodes.split(",")
        .map { it.trim() }
        .filter { it.isNotBlank() },
)
